import { useEffect, useState } from "react";
import { Icon, Sheet } from "./ui";

type Platform = "iphone" | "android";

const STEPS: Record<Platform, { title: string; text: string }[]> = {
  iphone: [
    { title: "In Safari öffnen", text: "Öffnen Sie HandVero in Safari, dem blauen Kompass-Symbol, und melden Sie sich an. Mit anderen Browsern klappt es nicht auf jedem iPhone." },
    { title: "Auf „Teilen“ tippen", text: "Das ist das Symbol mit dem Quadrat und dem Pfeil nach oben, unten in der Mitte (bei manchen iPhones oben rechts)." },
    { title: "„Zum Home-Bildschirm“ wählen", text: "Scrollen Sie in der Liste ein Stück nach unten, bis „Zum Home-Bildschirm“ erscheint, und tippen Sie darauf." },
    { title: "„Hinzufügen“ tippen", text: "Tippen Sie oben rechts auf „Hinzufügen“. HandVero liegt jetzt als Symbol auf Ihrem Bildschirm und startet wie jede andere App." },
  ],
  android: [
    { title: "In Chrome öffnen", text: "Öffnen Sie HandVero in Chrome und melden Sie sich an. Bei anderen Browsern sieht das Menü etwas anders aus." },
    { title: "Menü öffnen", text: "Tippen Sie oben rechts auf die drei Punkte." },
    { title: "„App installieren“ wählen", text: "Tippen Sie auf „App installieren“. Steht dort stattdessen „Zum Startbildschirm hinzufügen“, wählen Sie das." },
    { title: "Bestätigen", text: "Bestätigen Sie mit „Installieren“ oder „Hinzufügen“. HandVero liegt jetzt als Symbol auf Ihrem Bildschirm und startet wie jede andere App." },
  ],
};

function guess(): Platform {
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ? "iphone" : "android";
}

/** Läuft die Seite schon als App vom Startbildschirm? */
export function isInstalled(): boolean {
  try {
    return window.matchMedia("(display-mode: standalone)").matches || (navigator as Navigator & { standalone?: boolean }).standalone === true;
  } catch {
    return false;
  }
}

/** Anleitung, wie man HandVero auf den Startbildschirm des Handys bekommt, getrennt für iPhone und Android. */
export function InstallGuide({ onClose }: { onClose: () => void }) {
  const [platform, setPlatform] = useState<Platform>("android");
  useEffect(() => setPlatform(guess()), []);
  return (
    <Sheet title="HandVero auf den Startbildschirm" onClose={onClose}>
      <div className="install">
        <div className="choice" role="group" aria-label="Welches Handy?">
          <div>
            {(["iphone", "android"] as const).map((p) => (
              <button key={p} type="button" className={platform === p ? "on" : undefined} aria-pressed={platform === p} onClick={() => setPlatform(p)}>
                {p === "iphone" ? "iPhone (Apple)" : "Android"}
              </button>
            ))}
          </div>
        </div>
        <ol className="install-steps">
          {STEPS[platform].map((s, i) => (
            <li key={s.title}>
              <span className="install-num">{i + 1}</span>
              <span>
                <b>{s.title}</b>
                <small>{s.text}</small>
              </span>
            </li>
          ))}
        </ol>
        <p className="muted-s">Danach öffnet sich HandVero ohne Adresszeile, so wie eine App aus dem Store. Ihre Anmeldung bleibt erhalten.</p>
      </div>
    </Sheet>
  );
}

/** Eintrag für Einstellungen und Startseite der Handy-App. */
export function InstallGuideLink({ onOpen }: { onOpen: () => void }) {
  return (
    <button type="button" className="settings-account" onClick={onOpen}>
      <Icon name="grid" />
      <span>
        <b>App auf den Startbildschirm</b>
        <small>Anleitung für iPhone und Android</small>
      </span>
      <Icon name="chev" />
    </button>
  );
}
